import { C } from "../constants";
import { Sec } from "../components/Shared";

const h2Style = {
  fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 22,
  color: C.text, margin: "40px 0 16px", lineHeight: 1.3
};
const h3Style = {
  fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: 17,
  color: C.text, margin: "28px 0 10px", lineHeight: 1.3
};
const pStyle = {
  fontFamily: "'Nunito', sans-serif", fontSize: 15, color: C.textMid,
  lineHeight: 1.75, margin: "0 0 14px"
};
const liStyle = {
  fontFamily: "'Nunito', sans-serif", fontSize: 15, color: C.textMid,
  lineHeight: 1.75, marginBottom: 6
};
const linkStyle = { color: C.blue, fontWeight: 600, textDecoration: "none" };

export default function Terms() {
  return (
    <Sec>
      <div style={{ maxWidth: 740, margin: "0 auto" }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${C.blueLight}, ${C.bluePale})`,
          borderRadius: 16, padding: "32px 36px", marginBottom: 36,
          border: `1px solid ${C.border}`
        }}>
          <h1 style={{
            fontFamily: "'Nunito', sans-serif", fontWeight: 900,
            fontSize: "clamp(26px, 4vw, 34px)", color: C.text, margin: "0 0 12px"
          }}>SafeZapp Terms of Service</h1>
          <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: 14, color: C.textMid, margin: "0 0 16px" }}>
            Last updated: May 2026
          </p>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10, background: C.white, borderRadius: 10, padding: "14px 18px",
            border: `1px solid ${C.border}`
          }}>
            {[
              ["App name", "SafeZapp"],
              ["Developed By", "Dogmatech Development Solutions, Australia."],
              ["Platform", "Android (Google Play) and iOS (App Store), Australia"],
              ["Contact", "support@safezapp.app"],
            ].map(([label, value]) => (
              <div key={label}>
                <div style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: 12, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</div>
                <div style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 600, fontSize: 14, color: C.text }}>
                  {label === "Contact" ? <a href="mailto:support@safezapp.app" style={linkStyle}>{value}</a> : value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 1 */}
        <h2 style={h2Style}>1. Acceptance of Terms</h2>
        <p style={pStyle}>By downloading, installing, or using SafeZapp, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use SafeZapp.</p>
        <p style={pStyle}>If you are under 18, your guardian, carer, or other trusted adult ("Approver") must agree to these Terms on your behalf before you can use SafeZapp.</p>

        {/* 2 */}
        <h2 style={h2Style}>2. Description of Service</h2>
        <p style={pStyle}>SafeZapp is a supervised messaging app that enables individuals to communicate with approved contacts under the oversight of a trusted adult (the "Approver").</p>

        <h3 style={h3Style}>SafeZapp provides:</h3>
        <ul style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>Text-based messaging between approved contacts</li>
          <li style={liStyle}>Automated content safety analysis on all messages</li>
          <li style={liStyle}>Emoji-free messaging (emojis are not permitted in messages)</li>
          <li style={liStyle}>An Approver supervision model where all contacts must be approved before messaging is enabled</li>
          <li style={liStyle}>Approver visibility into all supervised chats</li>
          <li style={liStyle}>Account linking via pairing codes</li>
        </ul>

        <h3 style={h3Style}>SafeZapp does not provide:</h3>
        <ul style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>Media sharing (photos, videos, files)</li>
          <li style={liStyle}>Voice or video calls</li>
          <li style={liStyle}>Public profiles or user discovery</li>
          <li style={liStyle}>Advertising or sponsored content</li>
        </ul>

        {/* 3 */}
        <h2 style={h2Style}>3. Account Types</h2>

        <h3 style={h3Style}>3.1 Approver accounts</h3>
        <p style={pStyle}>Approver accounts are for adults (18 years or older) who supervise one or more Users. Approver accounts require a paid subscription (see section 6).</p>
        <p style={pStyle}>By creating an Approver account, you represent and warrant that:</p>
        <ul style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>You are at least 18 years of age</li>
          <li style={liStyle}>You have the legal authority to supervise the Users you link with</li>
          <li style={liStyle}>You will provide accurate information about your relationship to each User</li>
          <li style={liStyle}>You will accurately declare each User's age bracket when accepting a link request</li>
        </ul>

        <h3 style={h3Style}>3.2 User accounts</h3>
        <p style={pStyle}>User accounts are for individuals who use SafeZapp under the supervision of an Approver. User accounts are free.</p>
        <p style={pStyle}>A User account cannot be created without a valid pairing code generated by an Approver. By creating a User account, the User (and their Approver) agree to these Terms.</p>

        {/* 4 */}
        <h2 style={h2Style}>4. Acceptable Use</h2>
        <p style={pStyle}>You agree to use SafeZapp only for lawful purposes and in accordance with these Terms. You must not:</p>
        <ul style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>Use SafeZapp for any illegal, harmful, or abusive purpose</li>
          <li style={liStyle}>Send messages that are threatening, harassing, defamatory, obscene, or otherwise objectionable</li>
          <li style={liStyle}>Attempt to circumvent the Approver supervision model or any safety features</li>
          <li style={liStyle}>Impersonate any person or misrepresent your identity or relationship to a User</li>
          <li style={liStyle}>Attempt to gain unauthorised access to other users' accounts or data</li>
          <li style={liStyle}>Use SafeZapp to distribute spam, malware, or other harmful content</li>
          <li style={liStyle}>Reverse engineer, decompile, or disassemble any part of the app</li>
          <li style={liStyle}>Use the app in any way that could damage, disable, or impair the service</li>
        </ul>
        <p style={pStyle}>All messages are subject to automated safety analysis. Messages deemed unsafe may be blocked. SafeZapp may suggest alternative wording in some cases. Repeated attempts to send inappropriate content may result in account action.</p>
        <p style={pStyle}>We reserve the right to suspend or terminate accounts that violate these Terms.</p>

        {/* 5 */}
        <h2 style={h2Style}>5. The Approver Supervision Model</h2>

        <h3 style={h3Style}>5.1 Approver responsibilities</h3>
        <p style={pStyle}>As an Approver, you are responsible for:</p>
        <ul style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>Generating pairing codes and providing them only to Users you intend to supervise</li>
          <li style={liStyle}>Reviewing and approving or declining contact requests from your supervised Users</li>
          <li style={liStyle}>Reviewing and managing your supervised Users' approved contacts, including removing contacts if necessary</li>
          <li style={liStyle}>Monitoring conversations in supervised chats as you see fit</li>
          <li style={liStyle}>Accurately declaring each User's age bracket and your relationship to them</li>
          <li style={liStyle}>Managing your supervised Users' accounts, including unlinking them if necessary</li>
        </ul>

        <h3 style={h3Style}>5.2 Approver consent</h3>
        <p style={pStyle}>When an Approver accepts a link request, they must:</p>
        <ol style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>Declare the User's age bracket (under 13, 13–17, or 18+)</li>
          <li style={liStyle}>Confirm their relationship to the User (e.g. guardian, carer, or trusted person)</li>
        </ol>
        <p style={pStyle}>These declarations are recorded with a timestamp. By making these declarations, the Approver confirms that the information is accurate and that they have the authority to supervise the User.</p>

        <h3 style={h3Style}>5.3 Limitations</h3>
        <p style={pStyle}>SafeZapp provides tools for supervision, but does not guarantee the safety of any communication. The Approver is ultimately responsible for overseeing their Users' activity within the app.</p>

        {/* 6 */}
        <h2 style={h2Style}>6. Subscriptions and Payments</h2>

        <h3 style={h3Style}>6.1 Approver subscriptions</h3>
        <p style={pStyle}>Approver accounts require a paid subscription to enable messaging for their supervised Users. Subscription plans, pricing, and included features are displayed within the app and may vary. Subscriptions are managed through the Google Play Store.</p>

        <h3 style={h3Style}>6.2 Free trial</h3>
        <p style={pStyle}>New Approver accounts may receive a free trial period. The duration and terms of free trials are displayed at the time of sign-up and may change at our discretion.</p>

        <h3 style={h3Style}>6.3 Promotional offers</h3>
        <p style={pStyle}>From time to time, we may offer promotional codes that provide free access or extended subscription periods. Promotional codes are subject to their own terms, including expiry dates and usage limits. We reserve the right to modify or withdraw promotional offers at any time.</p>

        <h3 style={h3Style}>6.4 Effect of subscription expiry</h3>
        <p style={pStyle}>If an Approver's subscription expires:</p>
        <ul style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>The Approver's supervised Users will not be able to send or receive messages</li>
          <li style={liStyle}>Existing data (messages, contacts, account information) will be retained</li>
          <li style={liStyle}>Messaging will resume when the subscription is renewed</li>
        </ul>

        <h3 style={h3Style}>6.5 Refunds</h3>
        <p style={pStyle}>Refunds are handled in accordance with Google Play Store policies. We do not process refunds directly.</p>

        {/* 7 */}
        <h2 style={h2Style}>7. Account Deletion</h2>
        <p style={pStyle}>You may delete your account at any time from the Profile screen within the app. When you delete your account:</p>
        <ol style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>A two-step confirmation explains the consequences</li>
          <li style={liStyle}>Your account data is archived for 90 days, then permanently deleted</li>
          <li style={liStyle}>Your Firebase Authentication account is deleted immediately (you can no longer log in)</li>
          <li style={liStyle}>If you are an Approver, all supervised Users are unlinked</li>
          <li style={liStyle}>If a User is unlinked and does not re-link to an Approver within 14 days, their account and all associated data are automatically deleted</li>
        </ol>
        <p style={pStyle}>For full details on data retention, see our <a href="/privacy" style={linkStyle}>Privacy Policy</a>.</p>

        {/* 8 */}
        <h2 style={h2Style}>8. Account Suspension and Termination by SafeZapp</h2>

        <h3 style={h3Style}>8.1 Grounds for suspension or termination</h3>
        <p style={pStyle}>We may suspend or permanently terminate any account if we reasonably believe that:</p>
        <ul style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>The account is being used in violation of these Terms or any applicable law</li>
          <li style={liStyle}>The account is being used to send spam, unsolicited messages, or automated content</li>
          <li style={liStyle}>The account appears to be operated by a bot, script, or other automated means</li>
          <li style={liStyle}>The account was created fraudulently or with false information</li>
          <li style={liStyle}>The account is being used to harass, abuse, or endanger other users</li>
          <li style={liStyle}>The account poses a risk to the safety, security, or integrity of the service or its users</li>
          <li style={liStyle}>The account has been inactive for an extended period and has no active approver relationship</li>
        </ul>

        <h3 style={h3Style}>8.2 Notice</h3>
        <p style={pStyle}>Where practicable, we will notify you of the suspension or termination and the reason for it. In cases involving an immediate risk to user safety, security, or legal compliance, we may act without prior notice.</p>

        <h3 style={h3Style}>8.3 Effect of termination</h3>
        <p style={pStyle}>When an account is terminated by SafeZapp:</p>
        <ul style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>Access to the account will be revoked immediately</li>
          <li style={liStyle}>All associated data will be deleted in accordance with our data retention schedule</li>
          <li style={liStyle}>If the account is an Approver account, all supervised Users will be unlinked</li>
        </ul>

        <h3 style={h3Style}>8.4 Refunds on termination</h3>
        <p style={pStyle}>If your paid account is terminated due to a breach of these Terms (including fraud, abuse, or spam), no refund will be issued for any remaining subscription period.</p>
        <p style={pStyle}>If your paid account is terminated for any other reason, you may be entitled to a pro-rata refund for the unused portion of your current subscription period. Refunds are processed through the Google Play Store in accordance with their policies and applicable Australian Consumer Law.</p>

        <h3 style={h3Style}>8.5 Liability</h3>
        <p style={pStyle}>To the maximum extent permitted by law, we accept no liability for any loss or inconvenience arising from the suspension or termination of an account under this section. Nothing in this section excludes or limits any rights you may have under Australian Consumer Law.</p>

        {/* 9 */}
        <h2 style={h2Style}>9. Service Modification and Discontinuation</h2>
        <p style={pStyle}>We reserve the right to modify, suspend, or permanently discontinue SafeZapp — in whole or in part — at any time, including but not limited to commercial, technical, legal, or operational reasons.</p>

        <h3 style={h3Style}>9.1 Notice of discontinuation</h3>
        <p style={pStyle}>If we discontinue the service, we will provide at least 30 days' notice where practicable, via the app, email, or our website.</p>

        <h3 style={h3Style}>9.2 Effect of discontinuation</h3>
        <p style={pStyle}>If SafeZapp is discontinued:</p>
        <ul style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>All user accounts will be terminated and all data will be deleted in accordance with our data retention schedule</li>
          <li style={liStyle}>Any active paid subscriptions will not be renewed</li>
          <li style={liStyle}>Users with active paid subscriptions may be entitled to a pro-rata refund for the unused portion of their current subscription period, processed through the Google Play Store in accordance with their policies and applicable Australian Consumer Law</li>
        </ul>

        <h3 style={h3Style}>9.3 Liability</h3>
        <p style={pStyle}>To the maximum extent permitted by law, we accept no liability for any loss, inconvenience, or damage arising from the modification, suspension, or discontinuation of the service. Nothing in this section excludes or limits any rights you may have under Australian Consumer Law.</p>

        {/* 10 */}
        <h2 style={h2Style}>10. Intellectual Property</h2>
        <p style={pStyle}>All content, design, code, and trademarks associated with SafeZapp are the property of Dogmatech Development Solutions. You are granted a limited, non-exclusive, non-transferable licence to use the app for its intended purpose in accordance with these Terms.</p>
        <p style={pStyle}>You may not copy, modify, distribute, sell, or lease any part of SafeZapp without our prior written consent.</p>

        {/* 11 */}
        <h2 style={h2Style}>11. Disclaimers</h2>
        <p style={pStyle}>SafeZapp is provided on an "as is" and "as available" basis. To the maximum extent permitted by law, Dogmatech Development Solutions disclaims all warranties, express or implied, including but not limited to:</p>
        <ul style={{ paddingLeft: 24, margin: "0 0 14px" }}>
          <li style={liStyle}>Implied warranties of merchantability and fitness for a particular purpose</li>
          <li style={liStyle}>Warranties that the service will be uninterrupted, error-free, or secure</li>
          <li style={liStyle}>Warranties regarding the accuracy or reliability of any information obtained through the service</li>
        </ul>
        <p style={pStyle}>SafeZapp is a communication tool with supervision features. It is not a substitute for active oversight by an approver and does not guarantee the safety of any user.</p>

        {/* 12 */}
        <h2 style={h2Style}>12. Limitation of Liability</h2>
        <p style={pStyle}>To the maximum extent permitted by Australian law, Dogmatech Development Solutions shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of SafeZapp.</p>
        <p style={pStyle}>Our total liability to you for any claim arising from your use of SafeZapp shall not exceed the amount you have paid us in the twelve (12) months preceding the claim.</p>
        <p style={pStyle}>Nothing in these Terms excludes or limits liability that cannot be excluded or limited under Australian Consumer Law.</p>

        {/* 13 */}
        <h2 style={h2Style}>13. Privacy</h2>
        <p style={pStyle}>Your use of SafeZapp is also governed by our <a href="/privacy" style={linkStyle}>Privacy Policy</a>, which explains how we collect, use, and protect your personal information.</p>

        {/* 14 */}
        <h2 style={h2Style}>14. Changes to These Terms</h2>
        <p style={pStyle}>We may update these Terms from time to time. If we make material changes, we will notify you through the app or by other appropriate means. The "Last updated" date at the top of these Terms indicates when they were last revised.</p>
        <p style={pStyle}>Your continued use of SafeZapp after any changes constitutes your acceptance of the updated Terms.</p>

        {/* 15 */}
        <h2 style={h2Style}>15. Governing Law</h2>
        <p style={pStyle}>These Terms are governed by and construed in accordance with the laws of Australia. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts of Australia.</p>

        {/* 16 */}
        <h2 style={h2Style}>16. Contact Us</h2>
        <p style={pStyle}>If you have any questions about these Terms, please contact us at:</p>
        <div style={{
          background: C.blueLight, borderRadius: 12, padding: "16px 20px",
          border: `1px solid ${C.border}`, marginBottom: 14
        }}>
          <p style={{ ...pStyle, margin: 0 }}>
            Email: <a href="mailto:support@safezapp.app" style={linkStyle}>support@safezapp.app</a><br />
            Website: <a href="https://safezapp.app" style={linkStyle}>https://safezapp.app</a>
          </p>
        </div>

        <div style={{
          background: C.bg, borderRadius: 10, padding: "14px 18px", marginTop: 20,
          border: `1px solid ${C.border}`
        }}>
          <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: 13, color: C.textLight, margin: 0, fontStyle: "italic" }}>
            These Terms of Service apply to the SafeZapp application distributed on the Google Play Store and Apple App Store in Australia.
          </p>
        </div>
      </div>
    </Sec>
  );
}
